import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { hasPerm } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/Combobox";
import { RegiaoSugestaoHint } from "@/pages/bairros/RegiaoSugestaoHint";
import { useRegiaoSugestao } from "@/pages/bairros/useRegiaoSugestao";
import { toast } from "sonner";
import { Plus } from "lucide-react";

// QuickAddDialog — modal minimalista para cadastrar um item de catálogo sem sair
// da tela de origem. Uso pareado com SelectWithAdd.
// Props:
//   resource: rota da API ("bairros" | "regioes" | "tipos-entrega" | "formas-pgto")
//   label: nome amigável (ex: "Bairro")
//   extras: [{ key, label, options }] campos select adicionais
//   onAdded(doc): callback com o documento recém-criado (id + nome)

export function QuickAddDialog({ open, onOpenChange, resource, endpoint, label, extras = [], onAdded }) {
  const [nome, setNome] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const [manual, setManual] = useState({});
  const [saving, setSaving] = useState(false);
  const apiPath = endpoint || resource;
  const sugereRegiao = resource === "bairros" && extras.some((ex) => ex.key === "regiao_id");
  // Bairro: sugere a Região pela posição (até 2 km do centro = Centro; depois pela direção) enquanto o usuário não escolher manualmente.
  const sugestao = useRegiaoSugestao(nome, { enabled: open && sugereRegiao });
  const sugeridaId = sugestao.status === "ok" ? sugestao.data?.sugestao?.regiao_id : null;
  useEffect(() => { if (sugeridaId && !manual.regiao_id) setExtraValues((prev) => ({ ...prev, regiao_id: sugeridaId })); }, [sugeridaId, manual.regiao_id]);

  useEffect(() => { if (open) { setNome(""); setExtraValues({}); setManual({}); } }, [open]);

  const submit = async () => {
    const n = nome.trim();
    if (!n) { toast.error("Informe o nome"); return; }
    const faltando = extras.find((ex) => ex.required && !extraValues[ex.key]);
    if (faltando) { toast.error(`Selecione ${faltando.label}`); return; }
    setSaving(true);
    try {
      const payload = { nome: n, ...extraValues };
      const r = await api.post(`/${apiPath}`, payload);
      toast.success(`${label} "${n}" cadastrado`);
      onAdded?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erro ao cadastrar ${label}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`dialog-quickadd-${resource}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-orange-600" />Novo {label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome</Label>
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={`Nome do ${label.toLowerCase()}`}
              autoFocus
              data-testid={`quickadd-${resource}-nome`}
              onKeyDown={(e) => { if (e.key === "Enter" && nome.trim()) submit(); }}
            />
          </div>
          {extras.map((ex) => (
            <div key={ex.key}>
              <Label>{ex.label} {ex.required ? <span className="text-rose-600" aria-hidden>*</span> : <span className="text-xs text-muted-foreground">(opcional)</span>}</Label>
              <Select value={extraValues[ex.key] || ""} onValueChange={(v) => { setExtraValues((prev) => ({ ...prev, [ex.key]: v })); setManual((m) => ({ ...m, [ex.key]: true })); }}>
                <SelectTrigger data-testid={`quickadd-${resource}-${ex.key}`} className={ex.required && !extraValues[ex.key] ? "border-rose-300" : ""}>
                  <SelectValue placeholder={ex.required ? "Selecione…" : "—"} />
                </SelectTrigger>
                <SelectContent>
                  {ex.options.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              {ex.key === "regiao_id" && sugereRegiao && <RegiaoSugestaoHint sugestao={sugestao} aplicada={!!sugeridaId && extraValues.regiao_id === sugeridaId && !manual.regiao_id} testId={`quickadd-${resource}-regiao-hint`} />}
              {ex.required && ex.options.length === 0 && <p className="text-xs text-amber-700 mt-1">Nenhuma região cadastrada — cadastre uma região primeiro.</p>}
            </div>
          ))}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={saving || !nome.trim() || extras.some((ex) => ex.required && !extraValues[ex.key])}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid={`quickadd-${resource}-save`}
            >
              {saving ? "Salvando…" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// SelectWithAdd — wrapper que combina um Select/Combobox com um botão "+" ao lado.
// O botão só aparece se o user tem permissão de INCLUIR no `resource` (chave da perms_matrix).
// Props:
//   user
//   resource: chave da perms_matrix (ex: "bairros", "regioes", "tipos_entrega", "formas_pgto")
//   endpoint: path da API para POST (ex: "bairros", "regioes", "tipos-entrega", "formas-pgto")
//   label, options, value, onChange
//   onCatalogAdd(resource, doc): callback pra atualizar a lista local no parent
//   afterAdded(doc): callback opcional pra side-effects (auto-preencher regiao_id etc)
//   useCombobox: renderiza Combobox em vez de Select (para listas grandes tipo bairros)
//   extras: campos extras no modal (ex: regiao_id ao criar Bairro)
//   testId: data-testid do select/combobox
//   labelSlot: rótulo do Label (ex: "Bairro")
export function SelectWithAdd({
  user,
  resource,
  endpoint,
  label,
  options,
  value,
  onChange,
  onCatalogAdd,
  afterAdded,
  useCombobox = false,
  extras = [],
  testId,
  labelSlot,
}) {
  const [open, setOpen] = useState(false);
  const canAdd = hasPerm(user, resource, "i");
  const apiPath = endpoint || resource;

  return (
    <div>
      {labelSlot && <Label>{labelSlot}</Label>}
      <div className="flex gap-1 items-center">
        <div className="flex-1 min-w-0">
          {useCombobox ? (
            <Combobox
              options={options}
              value={value}
              onChange={onChange}
              placeholder="—"
              testId={testId}
            />
          ) : (
            <Select value={value} onValueChange={onChange}>
              <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        {canAdd && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setOpen(true)}
            className="h-9 w-9 shrink-0 border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800"
            title={`Cadastrar novo ${label.toLowerCase()}`}
            aria-label={`Cadastrar novo ${label.toLowerCase()}`}
            data-testid={`btn-quickadd-${resource}`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>
      {canAdd && (
        <QuickAddDialog
          open={open}
          onOpenChange={setOpen}
          resource={resource}
          endpoint={apiPath}
          label={label}
          extras={extras}
          onAdded={(doc) => {
            onCatalogAdd?.(resource, doc);
            onChange(doc.id);
            afterAdded?.(doc);
          }}
        />
      )}
    </div>
  );
}
